import { parseResponseError } from '@/utils/api';

const PIXEL_LISTS = {
  facebook_pixels: 'Facebook',
  tiktok_pixels: 'TikTok',
  kwai_pixels: 'Kwai',
  google_ads_pixels: 'Google Ads',
  taboola_pixels: 'Taboola',
  outbrain_pixels: 'Outbrain',
};

// O motivo da recusa do token volta repetido em `apiToken` e em `non_field_errors` (CKTPL-1663):
// o campo específico vem antes, para a tela apontar o que corrigir.
const ITEM_FIELD_ORDER = ['pixelId', 'apiToken', 'conversionLabel', 'name', 'non_field_errors'];

// O back-end testa o pixel e o token num evento só e pendura a recusa no `apiToken`, até a do pixel
// que a plataforma não achou (`pixel_request_rejected`, 30/10/2026). Sem código do motivo na API, a
// frase é o contrato: é por ela que a recusa volta para o campo do Pixel ID, e o status da linha
// casa a mesma frase.
export const PIXEL_NOT_FOUND_PATTERN = /pixel n[ãa]o encontrado/i;

const SAVE_REFUSED_MESSAGE = 'Não foi possível salvar os pixels. Confira os dados e tente de novo.';

function firstMessage(value) {
  if (typeof value === 'string') return value.trim() || undefined;
  if (Array.isArray(value)) return value.map(firstMessage).find(Boolean);
  if (value && typeof value === 'object')
    return Object.values(value).map(firstMessage).find(Boolean);
  return undefined;
}

function conversionFieldLabel(field) {
  if (/PixConversionValue$/.test(field)) return 'Valor de conversão do Pix';
  if (/BoletoConversionValue$/.test(field)) return 'Valor de conversão do boleto';
  return undefined;
}

function itemError(item) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return undefined;
  const field = [...ITEM_FIELD_ORDER, ...Object.keys(item)].find((key) => firstMessage(item[key]));
  if (!field) return undefined;

  const reason = firstMessage(item[field]);
  return { field: PIXEL_NOT_FOUND_PATTERN.test(reason) ? 'pixelId' : field, reason };
}

function listError(listKey, value, sentPixels) {
  const platform = PIXEL_LISTS[listKey];
  const index = Array.isArray(value) ? value.findIndex((item) => itemError(item)) : -1;

  if (index < 0) {
    const reason = firstMessage(value);
    return { message: `${platform}: ${reason}`, reason, list: listKey };
  }

  const { field, reason } = itemError(value[index]);
  // O índice é o da lista enviada, não o da tela (a tela tira as linhas vazias antes do save):
  // o Pixel ID enviado é o que o produtor reconhece.
  const pixelId = sentPixels?.[listKey]?.[index]?.pixelId;
  const where = pixelId ? `pixel ${pixelId}` : `linha ${index + 1}`;

  return { message: `${platform}, ${where}: ${reason}`, reason, list: listKey, index, field };
}

/**
 * Traduz a recusa do PUT de pixels numa mensagem em português que diz o que corrigir, e no campo
 * que a causou. Nunca devolve o `error.message` do axios ("Request failed with status code 400").
 */
export function parsePixelsSaveError(error, sentPixels) {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status !== 400) return { message: parseResponseError(error).message };
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return { message: firstMessage(data) || SAVE_REFUSED_MESSAGE };
  }

  const listKey = Object.keys(PIXEL_LISTS).find((key) => firstMessage(data[key]));
  if (listKey) return listError(listKey, data[listKey], sentPixels);

  const conversionField = Object.keys(data).find(
    (key) => conversionFieldLabel(key) && firstMessage(data[key])
  );
  if (conversionField) {
    const reason = firstMessage(data[conversionField]);
    return {
      message: `${conversionFieldLabel(conversionField)}: ${reason}`,
      reason,
      field: conversionField,
    };
  }

  return {
    message:
      firstMessage(data.non_field_errors) ||
      firstMessage(data.detail) ||
      firstMessage(data) ||
      SAVE_REFUSED_MESSAGE,
  };
}
